class UiForm extends HTMLElement {
  errors: any = {};
  form?: HTMLFormElement | null;
  noSubmit = false;

  constructor() {
    super();
  }

  connectedCallback() {
    this.form = this.querySelector("form");
    this.form?.addEventListener("submit", this.handleSubmit.bind(this));
    this.addEventListener("keyup", this.handleKeyup.bind(this));
    this.addEventListener("change", this.handleChange.bind(this));

    const passwords = this.querySelectorAll("[data-showpassword]");
    passwords.forEach((password) => {
      password.addEventListener("click", this.handleShowPassword.bind(this));
    });
    if (this.hasAttribute("no-submit")) this.noSubmit = true;

    this.handlePasswordError();
  }

  handlePasswordError() {
    const passwordInputs = document.querySelectorAll('[name="customer[password]"]');

    const validations = [
      {
        pattern: /.{8,}/,
        message: "- Minimum 8 Characters",
      },
      {
        pattern: /\d+/,
        message: "- At least 1 number",
      },
      {
        pattern: /[\W_]/,
        message: "- At least 1 special character (!@#$%^&*)",
      },
      {
        pattern: /[A-Z]/,
        message: "- At least 1 uppercase letter",
      },
    ];

    passwordInputs.forEach((passwordInput) => {
      passwordInput.addEventListener("keyup", () => {
        const password = passwordInput.value;
        let errorMessage = "";

        validations.forEach((validation) => {
          if (!validation.pattern.test(password)) {
            errorMessage += `<li>${validation.message}</li>`;
          }
        });

        if (errorMessage === "") {
          const validationMessage = passwordInput
            .closest(".create-customer-form")
            .querySelector(".password-validation-message");
          validationMessage.innerHTML = "";
          passwordInput.classList.remove("invalid-password");
        } else {
          const validationMessage = passwordInput
            .closest(".create-customer-form")
            .querySelector(".password-validation-message");
          validationMessage.innerHTML =
            errorMessage === "" ? "" : `<p class="mb-0">Your password must contain:</p><ul>${errorMessage}</ul>`;
          passwordInput.classList.add("invalid-password");
        }
      });
    });
  }

  handleShowPassword(e: Event) {
    e.preventDefault();
    const passwordId = e.currentTarget.getAttribute("data-showpassword");
    const elem = document.getElementById(passwordId) as HTMLInputElement | null;
    const btn = e.currentTarget.closest("button") as HTMLElement | null;

    if (btn && elem?.type === "password") {
      elem.type = "text";
      btn.setAttribute("aria-label", "Hide password");
      btn.innerHTML = /* HTML */ `<ui-icon name="icon-eye-on"></ui-icon>`;
    } else if (btn && elem?.type === "text") {
      elem.type = "password";
      btn.setAttribute("aria-label", "Show password");
      btn.innerHTML = /* HTML */ `<ui-icon name="icon-eye-off"></ui-icon>`;
    }
  }

  handleKeyup(e: KeyboardEvent) {
    const field = e.target as HTMLInputElement | null;
    if (!field) return;

    const isRequiredAndEmpty = field.getAttribute("required") !== null && field.value?.length === 0;

    this.handleError(field, !isRequiredAndEmpty, "Field is required");
    this.setIsValidClass(field, !isRequiredAndEmpty);

    if (field.getAttribute("type") === "email") {
      return this.handleEmail(field);
    } else if (field.getAttribute("data-validate") === "name") {
      return this.handleName(field);
    } else if (field.getAttribute("data-validate") === "birthday") {
      return this.handleBirthday(field, e);
    }
  }

  handleTags() {
    const tagField: HTMLInputElement | null | undefined = this.form?.querySelector("[data-tags]");
    if (!tagField) return;

    const tagValues = [];

    this.form?.querySelectorAll("[data-tag]")?.forEach((tag) => {
      const tagName = tag.getAttribute("data-tag");
      if (tagName && tag.value) {
        tagValues.push(`${tagName}:${tag.value}`);
      }
    });

    if (tagValues.length) {
      tagField.value = tagValues.join(",");
    }
  }

  handleSubmit(e: Event) {
    e.preventDefault();
    if (!this.noSubmit) e.stopPropagation();

    this.handleTags();

    (this.querySelectorAll("input:not([type=hidden])") as NodeListOf<HTMLInputElement>)?.forEach((field) => {
      const name = field.getAttribute("name");
      const isRequiredAndEmpty = field.hasAttribute("required") && field.value?.length === 0;

      if (name && isRequiredAndEmpty) {
        this.errors[name] = "Field is required";
      }
    });

    if (Object.keys(this.errors)?.length) {
      this.showErrors();
      return false;
    }

    document.querySelector("[data-form-success]")?.classList.remove("hidden");

    if (!this.noSubmit) this.form?.submit();
  }

  handleChange(e: Event) {
    const field = e.target as HTMLInputElement | null;

    if (field?.getAttribute("data-validate") === "birthday" && field.value !== "") {
      return this.handleBirthdayChange(field);
    }
  }

  day(field: HTMLInputElement) {
    return field.value?.split("/")?.[field.getAttribute("data-format") == "MM/DD" ? 1 : 0];
  }

  month(field: HTMLInputElement) {
    return field.value?.split("/")?.[field.getAttribute("data-format") == "MM/DD" ? 0 : 1];
  }

  handleBirthdayChange(field: HTMLInputElement) {
    if (field.value) {
      field.classList.add("has-value");
    } else {
      field.classList.remove("has-value");
    }

    // add a leading zero to the month
    if (this.day(field)?.length === 2 && this.month(field).length === 1) {
      field.value = field.getAttribute("data-format") == "MM/DD" ?
        `0${this.month(field)}/${this.day(field)}` :
        `${this.day(field)}/0${this.month(field)}`;

      this.handleBirthday(field);
    }
  }

  handleError(field: HTMLInputElement, isValid: boolean, message: string) {
    const name = field.getAttribute("name");
    if (name && !isValid && field.value.length) {
      this.errors[name] = message;
    } else if (name && this.errors[name] && isValid) {
      delete this.errors[name];
    }
  }

  handleName(field: HTMLInputElement) {
    const isValid = field.value?.length >= 2;
    this.handleError(field, isValid, "Name must be greater than 2 characters");
    this.setIsValidClass(field, isValid);
  }

  handleEmail(field: HTMLInputElement) {
    const value = field.value;
    const validEmail =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    const isValid = !!String(value).toLowerCase().match(validEmail)?.length;
    this.handleError(field, isValid, "Email is incorrect");
    this.setIsValidClass(field, isValid);
  }

  handleBirthday(field: HTMLInputElement, e?: KeyboardEvent) {
    const isUS = field.getAttribute("data-format") == "MM/DD";
    const validateDay = /^(0[1-9]|[12]\d|3[01])$/;
    const validateWholeDate = isUS ? 
      /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])$/ : 
      /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])$/;
    const invalidChars = /[^0-9\/]/;

    field.value = field.value?.replace(invalidChars, "");
    field.value = field.value?.replace("//", "/");
    field.value = field.value?.substring(0, 5);

    // add zero for day number if needed
    if (e?.key === "/" && this.day(field).length === 1) {
      field.value = "0" + field.value;
    }
    // auto add backspace
    if (e?.key !== "Backspace" && !!String(field.value).match(validateDay)?.length) {
      field.value += "/";
    }

    const isValid = !!String(field.value).match(validateWholeDate)?.length;
    this.handleError(field, isValid, "Birthday is incorrect");
    this.setIsValidClass(field, isValid);
  }

  setIsValidClass(field: HTMLInputElement, isValid: boolean) {
    if (isValid) {
      field.classList.add("valid");
      field.removeAttribute("aria-invalid");
      field.closest("._field")?.querySelector(".form__message.text-brand-error")?.remove();
    } else {
      field.classList.remove("valid");
    }
  }

  showErrors() {
    Object.keys(this.errors)?.forEach((name) => {
      const field = this.querySelector(`[name="${name}"]`);
      const textHelper =
        field?.closest("._field")?.querySelector(".form__message.text-brand-error") || document.createElement("small");
      textHelper.className = "form__message text-brand-error";
      textHelper.innerText = this.errors[name];
      field?.closest("._field")?.append(textHelper);
      field?.setAttribute("aria-invalid", "true");
    });
  }
}

customElements.define("ui-form", UiForm);
